/**
 * BoardState — bridges the real design generator to the v3 board's slot model,
 * holding one design pool PER REVISION. The live generator fills whichever
 * revision is currently generating (`liveId`); other revisions keep their pools
 * frozen. Each pool: shown (slots) + reserve (held back, revealed by Roll) +
 * emptySlots. Roll/remove/add are pure data ops on a pool; only generation
 * touches the engine.
 */
import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { World } from "src/types/World";
import type { Editor } from "src/types/Editor";
import type { GeneratedItem } from "src/types/GeneratedItem";

export const SLOT_MAX = 10;
const BATCH_SIZE = 16;
const INITIAL_SHOWN = 8;
const INITIAL_EMPTY = 2;
const REVEAL_MS = 650;

interface Pool {
  shown: GeneratedItem[];
  reserve: GeneratedItem[];
  emptySlots: number;
}
const emptyPool = (): Pool => ({ shown: [], reserve: [], emptySlots: 0 });

const keyOf = (item: GeneratedItem, i: number) => item.rgWorldHashKey ?? String(i);

export class BoardState {
  private pools = new Map<string, Pool>();
  bookmarks = new Set<string>();
  /** keys of designs pinned against removal */
  locked = new Set<string>();
  /** keys of designs revealed by the most recent roll (for the "New" badge) */
  recentlyRolled = new Set<string>();
  rolling = false;
  rollingId: string | null = null;
  rollingCount = 0;
  private liveId: string | null = null;
  private pendingFull = false;
  private editor!: Editor;
  destroy!: () => void;

  constructor(editor: Editor) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.editor = editor;
    const d1 = reaction(
      () => this.editor.designGenerator.isRunning,
      (running, prev) => {
        if (prev && !running) this.onSettled();
      }
    );
    const d2 = reaction(
      () => this.editor.designGenerator.list.length,
      () => this.captureLive()
    );
    this.destroy = () => {
      d1();
      d2();
    };
  }

  pool(id: string): Pool {
    return this.pools.get(id) ?? emptyPool();
  }
  private ensure(id: string): Pool {
    let p = this.pools.get(id);
    if (!p) {
      p = emptyPool();
      this.pools.set(id, p);
    }
    return p;
  }
  hasPool(id: string) {
    return this.pools.has(id);
  }
  removePool(id: string) {
    this.pools.delete(id);
  }

  shownOf(id: string) {
    return this.pool(id).shown;
  }
  reserveCountOf(id: string) {
    return this.pool(id).reserve.length;
  }
  emptySlotsOf(id: string) {
    return this.pool(id).emptySlots;
  }
  totalOf(id: string) {
    const p = this.pool(id);
    return p.shown.length + p.emptySlots;
  }
  canAddSlot(id: string) {
    const p = this.pool(id);
    return p.shown.length + p.emptySlots < SLOT_MAX && p.emptySlots < p.reserve.length;
  }
  canRoll(id: string) {
    const p = this.pool(id);
    return !this.rolling && p.emptySlots > 0 && p.reserve.length > 0;
  }
  rollAmt(id: string) {
    const p = this.pool(id);
    return Math.min(p.emptySlots, p.reserve.length);
  }
  isRolling(id: string) {
    return this.rolling && this.rollingId === id;
  }
  generatingFull(id: string) {
    return this.pendingFull && this.liveId === id && this.editor.designGenerator.isRunning;
  }
  progress(id: string) {
    return this.liveId === id ? this.editor.designGenerator.progress : 1;
  }

  get totalDesigns() {
    let n = 0;
    this.pools.forEach((p) => {
      n += p.shown.length;
    });
    return n;
  }

  private split(p: Pool, list: GeneratedItem[]) {
    p.shown = list.slice(0, INITIAL_SHOWN);
    p.reserve = list.slice(INITIAL_SHOWN);
  }

  private captureLive() {
    if (!this.liveId || !this.pendingFull) return;
    this.split(this.ensure(this.liveId), this.editor.designGenerator.list);
  }

  private onSettled() {
    if (!this.liveId || !this.pendingFull) return;
    const p = this.ensure(this.liveId);
    this.split(p, this.editor.designGenerator.list);
    p.emptySlots = Math.min(INITIAL_EMPTY, SLOT_MAX - p.shown.length, p.reserve.length);
    this.pendingFull = false;
  }

  /** Reset a revision's pool into full-capture mode for an incoming batch. */
  prepareLive(id: string) {
    this.liveId = id;
    this.pendingFull = true;
    this.pools.set(id, emptyPool());
    this.rolling = false;
    this.rollingId = null;
    this.rollingCount = 0;
    this.recentlyRolled = new Set();
    this.editor.designGenerator.listSize = BATCH_SIZE;
  }

  /** Fill a revision's pool by generating from `world`. */
  generateInto(id: string, world: World | null) {
    this.prepareLive(id);
    this.editor.designGenerator.generate(world);
    this.commitIfIdle(id);
  }

  /**
   * If the generator didn't actually run (it early-returns on an unchanged
   * brief — same world, or an identical new revision), the just-cleared pool
   * would stay empty. Reuse the generator's existing list instead.
   */
  commitIfIdle(id?: string) {
    const target = id ?? this.liveId;
    const gen = this.editor.designGenerator;
    if (!target || this.liveId !== target || !this.pendingFull) return;
    if (!gen.isRunning && gen.list.length > 0) this.onSettled();
  }

  roll(id: string, count: number) {
    const p = this.pool(id);
    const amt = Math.min(p.emptySlots, count, p.reserve.length);
    if (amt <= 0 || this.rolling) return;
    this.rolling = true;
    this.rollingId = id;
    this.rollingCount = amt;
    setTimeout(() => this.finishRoll(id, amt), REVEAL_MS);
  }
  private finishRoll(id: string, amt: number) {
    runInAction(() => {
      const p = this.ensure(id);
      const taken = p.reserve.slice(0, amt);
      p.reserve = p.reserve.slice(amt);
      p.shown = [...taken, ...p.shown];
      p.emptySlots = Math.max(0, p.emptySlots - taken.length);
      this.recentlyRolled = new Set(taken.map((t) => t.rgWorldHashKey));
      this.rolling = false;
      this.rollingId = null;
      this.rollingCount = 0;
    });
  }

  addSlot(id: string) {
    if (this.canAddSlot(id)) this.ensure(id).emptySlots += 1;
  }
  removeSlot(id: string) {
    const p = this.ensure(id);
    if (p.emptySlots > 0) p.emptySlots -= 1;
  }
  remove(id: string, item: GeneratedItem) {
    const p = this.ensure(id);
    if (!p.shown.includes(item)) return;
    if (this.locked.has(item.rgWorldHashKey)) return;
    p.shown = p.shown.filter((d) => d !== item);
    p.reserve = [...p.reserve, item];
    p.emptySlots += 1;
  }
  toggleBookmark(key: string) {
    if (this.bookmarks.has(key)) this.bookmarks.delete(key);
    else this.bookmarks.add(key);
  }
  toggleLock(key: string) {
    if (this.locked.has(key)) this.locked.delete(key);
    else this.locked.add(key);
  }
}

export { keyOf };
